import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiTrendingUp, FiDollarSign, FiRepeat, FiCheckCircle } = FiIcons;

const WorkshopDays = () => {
  const days = [
    {
      day: 1,
      title: "2X Profits with Unlimited FREE Leads",
      pain: "Why am I always chasing leads?",
      icon: FiTarget,
      color: "from-blue-500 to-blue-600",
      borderColor: "border-blue-400/50",
      bgColor: "from-blue-900/20 to-blue-800/20",
      learns: [
        "Where leads are leaking silently",
        "How businesses lose money before sales even start",
        "How to capture leads from ads, content, events, referrals, WhatsApp & websites",
        "How to automate follow-ups without manual work"
      ],
      outcomes: [
        "Know exactly where leads are leaking",
        "Have a ready-to-use lead capture framework"
      ],
      worth: "₹9,999"
    },
    {
      day: 2,
      title: "4X Profits with Effortless Sales Conversion",
      pain: "Leads come… but sales don't.",
      icon: FiTrendingUp,
      color: "from-green-500 to-green-600",
      borderColor: "border-green-400/50",
      bgColor: "from-green-900/20 to-green-800/20",
      learns: [
        "Why offers fail even with good traffic",
        "How to convert without pressure selling",
        "How chat, WhatsApp & automation increase conversions",
        "How to remove buying resistance instantly"
      ],
      outcomes: [
        "Clear conversion blueprint",
        "Sales without chasing",
        "Better results with same leads"
      ],
      worth: "₹14,999"
    },
    {
      day: 3,
      title: "8X Profits with Upsells & High-Ticket Offers",
      pain: "Too many sales, still low profit.",
      icon: FiDollarSign,
      color: "from-yellow-500 to-yellow-600",
      borderColor: "border-yellow-400/50",
      bgColor: "from-yellow-900/20 to-yellow-800/20",
      learns: [
        "Why low AOV keeps businesses poor",
        "How to add upsells, order bumps & high-ticket offers",
        "How to structure high-ticket sales confidently",
        "How to increase revenue without increasing workload"
      ],
      outcomes: [
        "Higher cart value",
        "Bigger profits from same customers"
      ],
      worth: "₹19,999"
    },
    {
      day: 4,
      title: "16X Profits with Customer Retention & Recurring Revenue",
      pain: "Customers buy once and disappear.",
      icon: FiRepeat,
      color: "from-purple-500 to-purple-600",
      borderColor: "border-purple-400/50",
      bgColor: "from-purple-900/20 to-purple-800/20",
      learns: [
        "How to build recurring income streams",
        "How to turn buyers into long-term clients",
        "How reminders, loyalty & community increase lifetime value",
        "How to create predictable monthly revenue"
      ],
      outcomes: [
        "Stable income",
        "Repeat buyers",
        "Long-term business growth"
      ],
      worth: "₹24,999"
    }
  ];

  return (
    <section className="py-20 bg-gray-900">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              This 4-Day LIVE Workshop Fixes ALL 4 Profit Leaks
            </h2>
            <p className="text-xl text-yellow-400 font-semibold">— Step by Step</p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {days.map((day, index) => (
              <motion.div
                key={day.day}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className={`bg-gradient-to-br ${day.bgColor} border ${day.borderColor} rounded-2xl p-8 backdrop-blur-sm hover:scale-105 transition-all duration-300`}
              >
                {/* Day Header */}
                <div className="flex items-center space-x-4 mb-6">
                  <div className={`bg-gradient-to-r ${day.color} w-16 h-16 rounded-full flex items-center justify-center`}>
                    <span className="text-white text-2xl font-bold">{day.day}</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">
                      Day {day.day}: {day.title}
                    </h3>
                    <p className="text-red-300 italic">"{day.pain}"</p>
                  </div>
                </div>

                {/* What They'll Learn */}
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-white mb-3">What You'll Learn:</h4>
                  <ul className="space-y-2">
                    {day.learns.map((learn, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-gray-300">
                        <SafeIcon icon={FiCheckCircle} className="text-green-400 text-sm mt-1 flex-shrink-0" />
                        <span className="text-sm">{learn}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Outcomes */}
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-white mb-3">By the end of Day {day.day}:</h4>
                  <ul className="space-y-2">
                    {day.outcomes.map((outcome, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-gray-300">
                        <SafeIcon icon={FiCheckCircle} className="text-blue-400 text-sm mt-1 flex-shrink-0" />
                        <span className="text-sm font-medium">✔️ {outcome}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Worth */}
                <div className={`bg-gradient-to-r ${day.color} text-white px-4 py-2 rounded-lg text-center font-bold`}>
                  💰 Worth {day.worth}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkshopDays;